import { cn } from "../lib/cn";
import { LOGO_FULL, LOGO_MARK } from "./logo-paths";

export interface LogoProps {
  /** `full` = the official logo (car mark above the MOTOLYNK wordmark) + product badge; `mark` = the car only. */
  variant?: "full" | "mark";
  /** `dark` on light backgrounds, `light` on the ink sidebar, `onBrand` on the yellow storefront header (black badge). */
  tone?: "dark" | "light" | "onBrand";
  /** Product badge next to the logo, e.g. "MARKET". */
  product?: string;
  className?: string;
}

function Shape({ shape, className }: { shape: typeof LOGO_FULL; className: string }) {
  return (
    <svg
      viewBox={`0 0 ${shape.width} ${shape.height}`}
      className={cn("w-auto shrink-0", className)}
      aria-hidden
      focusable="false"
    >
      <path fillRule="evenodd" d={shape.d} />
    </svg>
  );
}

/**
 * The MOTOLYNK logo: the official artwork (traced to vector paths in `logo-paths.ts`), drawn in the tone's colour so
 * it works on white, on the black sidebar and on the yellow header.
 */
export function Logo({
  variant = "full",
  tone = "dark",
  product = "MARKET",
  className,
}: LogoProps) {
  const ink = tone === "light" ? "fill-ink-foreground" : "fill-ink";
  if (variant === "mark") {
    return (
      <span className={cn("inline-flex", className)} role="img" aria-label="MOTOLYNK">
        <Shape shape={LOGO_MARK} className={cn("h-6", ink)} />
      </span>
    );
  }
  return (
    <span
      className={cn("inline-flex items-center gap-2.5", className)}
      role="img"
      aria-label={product ? `MOTOLYNK ${product}` : "MOTOLYNK"}
    >
      <Shape shape={LOGO_FULL} className={cn("h-10", ink)} />
      {product && (
        <span
          className={cn(
            "rounded-md px-2 py-1 font-display text-xs font-bold tracking-wide",
            tone === "onBrand" ? "bg-ink text-brand" : "bg-brand text-brand-foreground",
          )}
          aria-hidden
        >
          {product}
        </span>
      )}
    </span>
  );
}
